import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import UserMenu from "./UserMenu";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import { WORKSPACES_ENABLED } from "../lib/featureFlags";
import NotificationBell from "./NotificationBell";
import { ScrollTrigger } from "../lib/motion";

/**
 * Nav — Phase 19.1b-i (v5 Science Lab spec).
 *
 * Fixed at top, 68px tall, white/90% surface with 12px backdrop blur.
 * Border-bottom opacity ramps from 0 → 1 once the page has scrolled
 * past 80px (driven by GSAP ScrollTrigger so it stays in sync with
 * Lenis smooth-scroll). Wordmark uses DM Sans; nav links use Inter.
 *
 * Phase I visibility fix — on the Landing page hero (dark), the grey
 * ink used everywhere else disappears into the dark background. When
 * we detect we're at the top of Landing (path === "/" AND
 * !scrolled), we flip the nav into a light-on-dark style: the
 * background stays transparent so the hero shows through, and every
 * text token (logo, nav links, hover state) switches to white so it
 * reads over the deep indigo. Once the user scrolls past 80px, the
 * nav settles onto the white surface and the ink returns to dark.
 */
export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    // Only the Landing page has a dark hero large enough to swallow
    // the nav. Every other route sits on a light background from
    // pixel 0, so the historic light-nav-with-dark-ink is correct.
    const onDarkHero = location.pathname === "/" && !scrolled;

    useEffect(() => {
        // ScrollTrigger.create emits update ticks as the scroll position
        // changes; we flip `scrolled` once the user is past 80px. This
        // integrates cleanly with Lenis (Lenis calls ScrollTrigger.update
        // on its own scroll events in motion.js).
        const trigger = ScrollTrigger.create({
            start: "80 top",
            end: "max",
            onUpdate: (self) => {
                const past = self.scroll() > 80;
                setScrolled((prev) => (prev !== past ? past : prev));
            },
        });
        // Initial state — needed for hard-refreshes at non-zero scroll.
        setScrolled((window.scrollY || 0) > 80);
        return () => trigger.kill();
    }, []);

    const linkClass = ({ isActive }) => {
        const base = "relative text-[13px] font-medium tracking-tight transition-colors duration-200 whitespace-nowrap";
        if (onDarkHero) {
            // Light-on-dark palette. Active link stays fully white,
            // idle links sit at 75% to keep the row visually grouped.
            return `${base} ${isActive ? "text-white" : "text-white/75 hover:text-white"}`;
        }
        return `${base} ${isActive ? "text-ink-1" : "text-ink-2 hover:text-ink-1"}`;
    };

    return (
        <header
            data-testid="top-nav"
            data-on-dark={onDarkHero ? "1" : "0"}
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 backdrop-blur-glass ${
                scrolled
                    ? "bg-surface/90 border-b border-border shadow-sm"
                    : onDarkHero
                        // Phase I visibility fix: sit as a solid dark bar over
                        // the deep-indigo hero so white text reads cleanly and
                        // the contrast audit can compute a stable background.
                        ? "bg-deep border-b border-white/10"
                        : "bg-surface/70 border-b border-transparent"
            }`}
            data-scrolled={scrolled ? "true" : "false"}
        >
            <div className="max-w-wide mx-auto px-6 sm:px-10 h-[68px] flex items-center justify-between gap-6 flex-nowrap">
                <Link
                    to="/"
                    className={`font-wordmark text-[22px] font-black hover:text-brand-light transition-colors whitespace-nowrap flex-shrink-0 tracking-tight ${onDarkHero ? "text-white" : "text-ink-1"}`}
                    data-testid="nav-logo"
                >
                    Tomauro
                </Link>

                <nav className="hidden md:flex items-center gap-8 flex-nowrap min-w-0">
                    <NavLink to="/analyses" className={linkClass} data-testid="nav-analyses">
                        Analyses
                    </NavLink>
                    <NavLink to="/how-it-works" className={linkClass} data-testid="nav-how-it-works">
                        How it works
                    </NavLink>
                    <Link
                        to="/analyses/new"
                        data-testid="nav-cta"
                        className="group ml-2 inline-flex items-center gap-1.5 h-10 px-5 bg-brand text-white rounded-md text-[13px] font-medium shadow-sm hover:bg-brand-dark hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ease-out whitespace-nowrap flex-shrink-0"
                    >
                        Run an analysis
                        <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-0.5">
                            →
                        </span>
                    </Link>
                    {WORKSPACES_ENABLED && <WorkspaceSwitcher />}
                    <NotificationBell />
                    <UserMenu />
                </nav>

                {/* Mobile compact CTA */}
                <div className="md:hidden flex items-center gap-2">
                    <UserMenu />
                    <Link
                        to="/analyses/new"
                        data-testid="nav-cta-mobile"
                        className="inline-flex items-center h-9 px-3.5 bg-brand text-white rounded-md text-[12px] font-medium"
                    >
                        Run analysis
                    </Link>
                </div>
            </div>
        </header>
    );
}
